import { useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  FolderOpen,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { Trans, useTranslation } from 'react-i18next'
import { currentLocale } from '@/lib/i18n'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Switch, Textarea } from '@/components/ui/misc'
import { EmptyState, Loading } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { cn } from '@/lib/utils'
import {
  DEFAULT_GROUP_NAME,
  FIELD_TYPES,
  FIELD_TYPE_LABELS,
  fieldTypeVariant,
  useCdpProperties,
  useCreateProperty,
  useDeleteProperty,
  usePropertyGroups,
  useRenamePropertyGroup,
  useReorderProperties,
  useUpdateProperty,
  type CustomProperty,
  type PropertyOption,
} from '@/hooks/use-cdp'
import { useMyPermissions } from '@/hooks/use-settings'
import { Field, QueryError } from './cdp-shared'

const SELECT_TYPES = new Set(['single_select', 'multi_select'])

interface PropertyForm {
  name: string
  fieldType: string
  groupName: string
  description: string
  isRequired: boolean
  options: PropertyOption[]
}

const EMPTY_FORM: PropertyForm = {
  name: '',
  fieldType: 'text',
  groupName: '',
  description: '',
  isRequired: false,
  options: [],
}

function groupKey(p: CustomProperty): string {
  return p.groupName || DEFAULT_GROUP_NAME
}

export function PropertiesTab() {
  const { t } = useTranslation('cdp')
  const propsQuery = useCdpProperties()
  const groupsQuery = usePropertyGroups()
  const createProp = useCreateProperty()
  const updateProp = useUpdateProperty()
  const deleteProp = useDeleteProperty()
  const renameGroup = useRenamePropertyGroup()
  const reorder = useReorderProperties()
  // Quản lý / Nhân viên mặc định chỉ có `cdp.view` → không hiện Thêm / Sửa / Xoá / Đổi tên nhóm / Sắp xếp
  // (backend đòi cdp.create / cdp.update / cdp.delete).
  const { data: perms } = useMyPermissions()
  const canCreate = !!perms?.has('cdp.create')
  const canUpdate = !!perms?.has('cdp.update')
  const canDelete = !!perms?.has('cdp.delete')

  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<CustomProperty | null>(null)
  const [form, setForm] = useState<PropertyForm>(EMPTY_FORM)
  const [deleteTarget, setDeleteTarget] = useState<CustomProperty | null>(null)
  const [renameTarget, setRenameTarget] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')

  const properties = propsQuery.data?.properties ?? []

  // Gom theo nhóm, giữ thứ tự sortOrder từ backend
  const grouped = useMemo(() => {
    const map = new Map<string, CustomProperty[]>()
    for (const p of properties) {
      const k = groupKey(p)
      if (!map.has(k)) map.set(k, [])
      map.get(k)!.push(p)
    }
    // Nhóm rỗng (backend không trả) sẽ không có; sắp xếp: "Chung" cuối, còn lại theo tên
    return [...map.entries()].sort(([a], [b]) => {
      if (a === DEFAULT_GROUP_NAME) return 1
      if (b === DEFAULT_GROUP_NAME) return -1
      return a.localeCompare(b, currentLocale())
    })
  }, [properties])

  const groupNames = useMemo(
    () => (groupsQuery.data?.groups ?? []).map((g) => g.name).filter((n) => n !== DEFAULT_GROUP_NAME),
    [groupsQuery.data],
  )

  function openCreate(groupName?: string) {
    setEditTarget(null)
    setForm({
      ...EMPTY_FORM,
      groupName: groupName && groupName !== DEFAULT_GROUP_NAME ? groupName : '',
    })
    setDialogOpen(true)
  }

  function openEdit(p: CustomProperty) {
    setEditTarget(p)
    setForm({
      name: p.name,
      fieldType: p.fieldType,
      groupName: p.groupName ?? '',
      description: p.description ?? '',
      isRequired: p.isRequired,
      options: Array.isArray(p.options) ? p.options.map((o) => ({ ...o })) : [],
    })
    setDialogOpen(true)
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    const name = form.name.trim()
    if (!name) {
      toast.error(t('properties.toast.nameRequired'))
      return
    }
    const isSelect = SELECT_TYPES.has(form.fieldType)
    const options = isSelect
      ? form.options
          .map((o) => ({
            value: o.value.trim(),
            label: o.label.trim() || o.value.trim(),
          }))
          .filter((o) => o.value)
      : []
    if (isSelect && options.length === 0) {
      toast.error(t('properties.toast.needOption'))
      return
    }
    const payload = {
      name,
      fieldType: form.fieldType,
      options,
      isRequired: form.isRequired,
      groupName: form.groupName.trim(),
      description: form.description.trim(),
    }
    try {
      if (editTarget) {
        await updateProp.mutateAsync({ id: editTarget.id, data: payload })
        toast.success(t('properties.toast.updated'))
      } else {
        await createProp.mutateAsync(payload)
        toast.success(t('properties.toast.created'))
      }
      setDialogOpen(false)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onDelete() {
    if (!deleteTarget) return
    try {
      await deleteProp.mutateAsync(deleteTarget.id)
      toast.success(t('properties.toast.deleted'))
      setDeleteTarget(null)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  async function onRename(e: React.FormEvent) {
    e.preventDefault()
    if (!renameTarget) return
    const newName = renameValue.trim()
    if (!newName) {
      toast.error(t('properties.toast.groupNameRequired'))
      return
    }
    try {
      await renameGroup.mutateAsync({ oldName: renameTarget, newName })
      toast.success(t('properties.toast.groupRenamed'))
      setRenameTarget(null)
    } catch (err) {
      toast.error(apiError(err))
    }
  }

  function move(list: CustomProperty[], index: number, dir: -1 | 1) {
    const target = index + dir
    if (target < 0 || target >= list.length) return
    const next = [...list]
    ;[next[index], next[target]] = [next[target], next[index]]
    reorder.mutate(
      next.map((p, i) => ({ id: p.id, sortOrder: i })),
      { onError: (err) => toast.error(apiError(err)) },
    )
  }

  const saving = createProp.isPending || updateProp.isPending
  const isSelectType = SELECT_TYPES.has(form.fieldType)

  if (propsQuery.isLoading) return <Loading label={t('properties.loading')} />
  if (propsQuery.isError) return <QueryError error={propsQuery.error} what={t('properties.what')} />

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('properties.summary', { properties: properties.length, groups: grouped.length })}
        </p>
        {canCreate && (
          <Button onClick={() => openCreate()}>
            <Plus /> {t('properties.add')}
          </Button>
        )}
      </div>

      {grouped.length === 0 ? (
        <EmptyState
          icon={FolderOpen}
          title={t('properties.empty.title')}
          description={t('properties.empty.description')}
          action={
            canCreate ? (
              <Button onClick={() => openCreate()}>
                <Plus /> {t('properties.add')}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
          {grouped.map(([name, list]) => {
            const isOpen = !collapsed[name]
            return (
              <Card key={name}>
                <div className="flex items-center gap-2 px-4 py-3">
                  <button
                    type="button"
                    className="flex min-w-0 flex-1 items-center gap-2 text-left"
                    onClick={() => setCollapsed((s) => ({ ...s, [name]: isOpen }))}
                  >
                    {isOpen ? (
                      <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                    )}
                    <span className="truncate font-semibold">{name}</span>
                    <Badge variant="secondary">{list.length}</Badge>
                  </button>
                  <div className="flex shrink-0 items-center gap-1">
                    {canUpdate && name !== DEFAULT_GROUP_NAME && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setRenameTarget(name)
                          setRenameValue(name)
                        }}
                      >
                        <Pencil className="!size-3.5" /> {t('properties.rename')}
                      </Button>
                    )}
                    {canCreate && (
                      <Button variant="ghost" size="sm" onClick={() => openCreate(name)}>
                        <Plus className="!size-3.5" /> {t('properties.addToGroup')}
                      </Button>
                    )}
                  </div>
                </div>

                {isOpen && (
                  <CardContent className="space-y-2 pt-0">
                    {list.map((p, i) => (
                      <div
                        key={p.id}
                        className="flex flex-wrap items-center gap-3 rounded-lg border bg-background px-3 py-2"
                      >
                        {canUpdate && (
                          <div className="flex shrink-0 flex-col">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              disabled={i === 0 || reorder.isPending}
                              onClick={() => move(list, i, -1)}
                              aria-label={t('properties.moveUp')}
                            >
                              <ArrowUp className="!size-3" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-5 w-5"
                              disabled={i === list.length - 1 || reorder.isPending}
                              onClick={() => move(list, i, 1)}
                              aria-label={t('properties.moveDown')}
                            >
                              <ArrowDown className="!size-3" />
                            </Button>
                          </div>
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium">{p.name}</span>
                            <code className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
                              {p.fieldKey}
                            </code>
                            <Badge variant={fieldTypeVariant(p.fieldType)}>
                              {FIELD_TYPE_LABELS[p.fieldType] ?? p.fieldType}
                            </Badge>
                            {p.isRequired && <Badge variant="outline">{t('properties.required')}</Badge>}
                          </div>
                          {p.description && (
                            <p className="mt-0.5 truncate text-xs text-muted-foreground">{p.description}</p>
                          )}
                          {SELECT_TYPES.has(p.fieldType) && Array.isArray(p.options) && p.options.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {p.options.slice(0, 8).map((o) => (
                                <Badge key={o.value} variant="secondary" className="font-normal">
                                  {o.label || o.value}
                                </Badge>
                              ))}
                              {p.options.length > 8 && (
                                <span className="text-xs text-muted-foreground">+{p.options.length - 8}</span>
                              )}
                            </div>
                          )}
                        </div>
                        {(canUpdate || canDelete) && (
                          <div className="flex shrink-0 items-center gap-0.5">
                            {canUpdate && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7"
                                onClick={() => openEdit(p)}
                                aria-label={t('properties.edit')}
                              >
                                <Pencil className="!size-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-destructive hover:text-destructive"
                                onClick={() => setDeleteTarget(p)}
                                aria-label={t('properties.delete')}
                              >
                                <Trash2 className="!size-3.5" />
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Dialog thêm/sửa thuộc tính */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTarget ? t('properties.dialog.editTitle') : t('properties.add')}</DialogTitle>
            {editTarget && (
              <DialogDescription>
                <Trans
                  t={t}
                  i18nKey="properties.dialog.renameWarning"
                  values={{ key: editTarget.fieldKey }}
                  components={{ code: <code /> }}
                />
              </DialogDescription>
            )}
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <Field label={t('properties.dialog.nameLabel')}>
              <Input
                value={form.name}
                onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
                placeholder={t('properties.dialog.namePlaceholder')}
                maxLength={100}
                autoFocus
              />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label={t('properties.dialog.typeLabel')}>
                <Select value={form.fieldType} onValueChange={(v) => setForm((s) => ({ ...s, fieldType: v }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FIELD_TYPES.map((ft) => (
                      <SelectItem key={ft} value={ft}>
                        {FIELD_TYPE_LABELS[ft]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <Field label={t('properties.dialog.groupLabel')} hint={t('properties.dialog.groupHint')}>
                <Input
                  list="cdp-group-names"
                  value={form.groupName}
                  onChange={(e) => setForm((s) => ({ ...s, groupName: e.target.value }))}
                  placeholder={DEFAULT_GROUP_NAME}
                  maxLength={50}
                />
                <datalist id="cdp-group-names">
                  {groupNames.map((g) => (
                    <option key={g} value={g} />
                  ))}
                </datalist>
              </Field>
            </div>
            <Field label={t('properties.dialog.descLabel')}>
              <Textarea
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder={t('properties.dialog.descPlaceholder')}
                className="min-h-[60px]"
              />
            </Field>

            {isSelectType && (
              <Field label={t('properties.dialog.optionsLabel')} hint={t('properties.dialog.optionsHint')}>
                <div className="space-y-2">
                  {form.options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Input
                        value={o.value}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            options: s.options.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)),
                          }))
                        }
                        placeholder={t('properties.dialog.optionValue')}
                        className="flex-1"
                      />
                      <Input
                        value={o.label}
                        onChange={(e) =>
                          setForm((s) => ({
                            ...s,
                            options: s.options.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)),
                          }))
                        }
                        placeholder={t('properties.dialog.optionLabel')}
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0"
                        onClick={() =>
                          setForm((s) => ({
                            ...s,
                            options: s.options.filter((_, j) => j !== i),
                          }))
                        }
                        aria-label={t('properties.dialog.removeOption')}
                      >
                        <X className="!size-3.5" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setForm((s) => ({
                        ...s,
                        options: [...s.options, { value: '', label: '' }],
                      }))
                    }
                  >
                    <Plus className="!size-3.5" /> {t('properties.dialog.addOption')}
                  </Button>
                </div>
              </Field>
            )}

            <div className="flex items-center justify-between rounded-lg border px-3 py-2">
              <div>
                <p className="text-sm font-medium">{t('properties.dialog.requiredLabel')}</p>
                <p className="text-xs text-muted-foreground">{t('properties.dialog.requiredHint')}</p>
              </div>
              <Switch checked={form.isRequired} onCheckedChange={(v) => setForm((s) => ({ ...s, isRequired: v }))} />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                {t('properties.cancel')}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="animate-spin" />}
                {editTarget ? t('properties.save') : t('properties.create')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog đổi tên nhóm */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('properties.renameDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="properties.renameDialog.description"
                values={{ name: renameTarget }}
                components={{ b: <span className="font-medium text-foreground" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onRename} className="space-y-4">
            <Input value={renameValue} onChange={(e) => setRenameValue(e.target.value)} maxLength={50} autoFocus />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameTarget(null)}>
                {t('properties.cancel')}
              </Button>
              <Button type="submit" disabled={renameGroup.isPending}>
                {renameGroup.isPending && <Loader2 className="animate-spin" />}
                {t('properties.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog xóa */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('properties.deleteDialog.title')}</DialogTitle>
            <DialogDescription>
              <Trans
                t={t}
                i18nKey="properties.deleteDialog.description"
                values={{ name: deleteTarget?.name }}
                components={{ b: <span className="font-medium text-foreground" /> }}
              />
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('properties.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={deleteProp.isPending}
              className={cn(deleteProp.isPending && 'opacity-80')}
            >
              {deleteProp.isPending && <Loader2 className="animate-spin" />}
              {t('properties.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
